import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EXPIRATION_LABELS, TIER_LIMITS } from '@reqshare/shared';
import type { ExpirationOption, UsageInfo } from '@reqshare/shared';
import { useWebAuth } from '../../hooks/useWebAuth';
import { useWebSettings } from '../../hooks/useWebSettings';
import { getGoogleAuthUrl, getGithubAuthUrl } from '../../api/auth-api';
import { getUsage } from '../../api/usage-api';
import { cancelSubscription } from '../../api/subscription-api';
import { ConfirmModal } from '../../components/ConfirmModal/ConfirmModal';
import { SettingsSection } from '../../components/SettingsSection/SettingsSection';
import { SettingsToggle } from '../../components/SettingsToggle/SettingsToggle';
import { SettingsSelect } from '../../components/SettingsSelect/SettingsSelect';
import './PreferencesPage.scss';

const EXPIRATION_OPTIONS = (Object.keys(EXPIRATION_LABELS) as ExpirationOption[]).map((key) => ({
  value: key,
  label: EXPIRATION_LABELS[key],
}));

const SHARE_FORMAT_OPTIONS = [
  { value: 'link', label: 'Link' },
  { value: 'text', label: 'Text/cURL' },
];

const THEME_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const CODE_FORMAT_OPTIONS = [
  { value: 'pretty', label: 'Pretty' },
  { value: 'raw', label: 'Raw' },
];

export function PreferencesPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading, logout } = useWebAuth();
  const { preferences, updatePreferences, loading: settingsLoading } = useWebSettings();
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const loading = authLoading || settingsLoading;
  const tier = user?.subscription?.tier ?? 'free';
  const isPro = tier === 'pro';

  useEffect(() => {
    if (!token || !isAuthenticated) return;
    getUsage(token).then(setUsage).catch(() => {});
  }, [token, isAuthenticated]);

  const handleCancelSubscription = async () => {
    if (!token) return;
    setCancelling(true);
    setCancelError('');
    try {
      await cancelSubscription(token);
      setCancelling(false);
      setShowCancelModal(false);
      navigate('/subscription/cancelled?until=' + encodeURIComponent(user?.subscription?.currentPeriodEnd ?? ''));
    } catch (err) {
      setCancelling(false);
      setCancelError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <main className="preferences-page">
      <div className="preferences-page__container">
        <header className="preferences-page__header">
          <h1 className="preferences-page__title">Preferences</h1>
          <p className="preferences-page__subtitle">
            Manage your account and customize shareq.site settings.
          </p>
        </header>

        <AuthBlock
          isAuthenticated={isAuthenticated}
          email={user?.email}
          onLogout={logout}
        />

        {isAuthenticated && (
          <div className="preferences-page__card">
            <SettingsSection title="Subscription" defaultOpen>
              <div className="preferences-page__subscription">
                <div className="preferences-page__subscription-tier">
                  <span className="preferences-page__subscription-label">Current plan</span>
                  <span className={`preferences-page__subscription-badge${isPro ? ' preferences-page__subscription-badge--pro' : ''}`}>
                    {isPro ? 'Pro' : 'Free'}
                  </span>
                  {isPro && (
                    <span className="preferences-page__subscription-interval">
                      ({(user?.subscription?.billingInterval ?? 'monthly') === 'yearly' ? 'Annual' : 'Monthly'})
                    </span>
                  )}
                </div>
                {!isPro && usage && (
                  <div className="preferences-page__subscription-usage">
                    {usage.dailySharesUsed}/{usage.dailySharesLimit} shares used today
                  </div>
                )}
                {isPro && user?.subscription?.status === 'cancelled' && user?.subscription?.currentPeriodEnd && (
                  <div className="preferences-page__subscription-usage">
                    Pro access until {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                  </div>
                )}
                {isPro && user?.subscription?.currentPeriodEnd && user?.subscription?.status === 'active' && (
                  <div className="preferences-page__subscription-usage">
                    Next billing: {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                  </div>
                )}
                {isPro && user?.subscription?.status === 'active' && (
                  <button
                    type="button"
                    className="preferences-page__subscription-cancel"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancel subscription
                  </button>
                )}
                {!isPro && (
                  <Link to="/pricing" className="preferences-page__subscription-upgrade">
                    Upgrade to Pro
                  </Link>
                )}
              </div>
            </SettingsSection>
          </div>
        )}

        {loading ? (
          <div className="preferences-page__card">
            <p className="preferences-page__loading">Loading settings...</p>
          </div>
        ) : (
          <div className="preferences-page__card">
            <SettingsSection title="Security & Privacy" defaultOpen>
              <SettingsToggle
                label="Show sensitive headers"
                checked={!preferences.security.maskSensitiveHeaders}
                onChange={(checked) =>
                  updatePreferences({ security: { maskSensitiveHeaders: !checked } })
                }
                disabled={!isAuthenticated}
              />
              {!preferences.security.maskSensitiveHeaders && (
                <SettingsToggle
                  label="Confirm before sharing sensitive headers"
                  checked={preferences.security.confirmBeforeSharing}
                  onChange={(checked) =>
                    updatePreferences({ security: { confirmBeforeSharing: checked } })
                  }
                  disabled={!isAuthenticated}
                />
              )}
            </SettingsSection>

            <SettingsSection title="Share Behavior" defaultOpen>
              <SettingsSelect
                label="Format"
                value={preferences.sharing.defaultShareFormat}
                options={SHARE_FORMAT_OPTIONS}
                onChange={(value) =>
                  updatePreferences({ sharing: { defaultShareFormat: value as 'link' | 'text' } })
                }
                disabled={!isAuthenticated}
              />
              <SettingsToggle
                label="Include headers"
                checked={preferences.sharing.includeHeaders}
                onChange={(checked) =>
                  updatePreferences({ sharing: { includeHeaders: checked } })
                }
                disabled={!isAuthenticated}
              />
              <SettingsToggle
                label="Include response data"
                checked={preferences.sharing.includeResponseData}
                onChange={(checked) =>
                  updatePreferences({ sharing: { includeResponseData: checked } })
                }
                disabled={!isAuthenticated}
              />
              <SettingsToggle
                label="Include request body"
                checked={preferences.sharing.includeRequestBody}
                onChange={(checked) =>
                  updatePreferences({ sharing: { includeRequestBody: checked } })
                }
                disabled={!isAuthenticated}
              />
              <SettingsToggle
                label="Include query parameters"
                checked={preferences.sharing.includeQueryParameters}
                onChange={(checked) =>
                  updatePreferences({ sharing: { includeQueryParameters: checked } })
                }
                disabled={!isAuthenticated}
              />
            </SettingsSection>

            <SettingsSection title="Expiration">
              <SettingsSelect
                label="Default expiration"
                value={preferences.expiration.defaultExpiration}
                options={EXPIRATION_OPTIONS}
                onChange={(value) =>
                  updatePreferences({ expiration: { defaultExpiration: value as ExpirationOption } })
                }
                disabled={!isAuthenticated}
              />
            </SettingsSection>

            <SettingsSection title="Appearance">
              <SettingsSelect
                label="Theme"
                value={preferences.appearance.theme}
                options={THEME_OPTIONS}
                onChange={(value) =>
                  updatePreferences({ appearance: { theme: value as 'system' | 'light' | 'dark' } })
                }
                disabled={!isAuthenticated}
              />
              <SettingsSelect
                label="Code format"
                value={preferences.appearance.codeFormat}
                options={CODE_FORMAT_OPTIONS}
                onChange={(value) =>
                  updatePreferences({ appearance: { codeFormat: value as 'pretty' | 'raw' } })
                }
                disabled={!isAuthenticated}
              />
            </SettingsSection>

            {!isAuthenticated && (
              <p className="preferences-page__hint">
                Sign in to save and sync your preferences.
              </p>
            )}
          </div>
        )}
      </div>
      {showCancelModal && user?.subscription?.currentPeriodEnd && (
        <ConfirmModal
          title="Cancel subscription"
          confirmLabel="Cancel subscription"
          cancelLabel="Keep subscription"
          onConfirm={handleCancelSubscription}
          onCancel={() => setShowCancelModal(false)}
          loading={cancelling}
        >
          <p>You'll keep Pro access until <strong>{new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}</strong>.</p>
          <p>After that, you'll be on the Free plan (5 shares/day, 10MB limit).</p>
          {cancelError && <p className="preferences-page__cancel-error">{cancelError}</p>}
        </ConfirmModal>
      )}
    </main>
  );
}

interface AuthBlockProps {
  isAuthenticated: boolean;
  email?: string;
  onLogout: () => void;
}

function AuthBlock({ isAuthenticated, email, onLogout }: AuthBlockProps) {
  if (isAuthenticated) {
    return (
      <div className="preferences-page__auth-card">
        <span className="preferences-page__auth-email">{email}</span>
        <button
          type="button"
          className="preferences-page__auth-logout"
          onClick={onLogout}
        >
          Sign Out
        </button>
      </div>
    );
  }

  const handleGoogleSignIn = () => {
    window.location.href = getGoogleAuthUrl();
  };

  const handleGithubSignIn = () => {
    window.location.href = getGithubAuthUrl();
  };

  return (
    <div className="preferences-page__auth-card">
      <span className="preferences-page__auth-label">Not signed in</span>
      <div className="preferences-page__auth-actions">
        <button
          type="button"
          className="preferences-page__auth-btn"
          onClick={handleGoogleSignIn}
        >
          Sign in with Google
        </button>
        <button
          type="button"
          className="preferences-page__auth-btn preferences-page__auth-btn--secondary"
          onClick={handleGithubSignIn}
        >
          Sign in with GitHub
        </button>
      </div>
    </div>
  );
}
